import { useEffect, useMemo, useState } from 'react';
import { api, type ProfitRouteRow } from '../lib/api';
import { useSticky } from '../lib/sticky';
import { ErrorBox, money } from '../components/DataTable';
import { useProfitMonths } from './profitMonths';
import { CalcNote } from '../components/CalcNote';

/**
 * 경영 분석 › 노선별 — 수익TOOL 「🚍노선별수익분석」 대체(10-24). 서버 `listProfitRoute`(`functions/src/profitOps.ts` ROUTE_SQL).
 * 일반 = 노선 한 줄 · 통근 = 접수 한 줄(통근 청구는 접수 단위라 노선으로 나누면 청구 0원 노선이 절반).
 * 「비수익만」 · 고객별 · 담당자별 건수 = 시트 오른쪽 두 표.
 */

type Kind = '일반' | '통근';
type View = 'list' | 'cust' | 'manager';
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const profitOf = (r: ProfitRouteRow) => Number(r.rev) - Number(r.exp) - Number(r.dalloc);

export default function ProfitRoute() {
  const [error, setError] = useState<string | null>(null);
  const [months, mon, setMon] = useProfitMonths(setError);
  const [kind, setKind] = useSticky<Kind>('profit.route.kind', '일반');
  const [view, setView] = useState<View>('list');
  const [onlyLoss, setOnlyLoss] = useState(true);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<ProfitRouteRow[] | null>(null);

  useEffect(() => {
    if (!mon) return;
    let cancelled = false;
    setRows(null); setError(null);
    api.listProfitRoute({ mon }).then((r) => { if (!cancelled) setRows(r.rows); }).catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [mon]);

  const mine = useMemo(() => (rows ?? []).filter((r) => r.kind === kind), [rows, kind]);
  const loss = useMemo(() => mine.filter((r) => profitOf(r) < 0), [mine]);
  const list = useMemo(() => {
    const w = q.trim();
    return (onlyLoss ? loss : mine).filter((r) => !w || r.cust_name.includes(w) || r.manager.includes(w) || (r.name ?? '').includes(w) || String(r.recv_id) === w)
      .sort((a, b) => profitOf(a) - profitOf(b));
  }, [mine, loss, onlyLoss, q]);
  const groups = useMemo(() => {
    const m = new Map<string, { key: string; n: number; loss: number }>();
    for (const r of loss) {
      const k = (view === 'manager' ? r.manager : r.cust_name) || '(없음)';
      const o = m.get(k) ?? { key: k, n: 0, loss: 0 };
      o.n++; o.loss += profitOf(r); m.set(k, o);
    }
    return [...m.values()].sort((a, b) => b.n - a.n || a.loss - b.loss);
  }, [loss, view]);
  const tot = useMemo(() => ({ n: mine.length, loss: loss.length, lossAmt: loss.reduce((a, r) => a + profitOf(r), 0) }), [mine, loss]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select className="field w-auto" value={mon} onChange={(e) => setMon(e.target.value)} aria-label="정산월">
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="flex gap-1">
          {(['일반', '통근'] as const).map((k) => <button key={k} className={kind === k ? 'btn-primary' : 'btn'} onClick={() => setKind(k)}>{k === '일반' ? '일반(노선)' : '통근(접수)'}</button>)}
        </div>
        <div className="flex gap-1">
          {([['list', '목록'], ['cust', '고객별'], ['manager', '버스인접수담당자별']] as const).map(([k, l]) => <button key={k} className={view === k ? 'btn-primary' : 'btn'} onClick={() => setView(k)}>{l}</button>)}
        </div>
        {view === 'list' && (
          <>
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={onlyLoss} onChange={(e) => setOnlyLoss(e.target.checked)} />비수익만</label>
            <input className="field w-48" placeholder="고객·버스인접수담당자·노선·접수번호" value={q} onChange={(e) => setQ(e.target.value)} />
          </>
        )}
        <CalcNote>청구상태가 있는 접수만(거래처별과 같은 범위). 일반은 노선 청구액 − 그 노선 운행료(영업 가감 포함) · 통근은 접수 청구액 − 운행료 − 직영차량 배분.
        「청구 0」은 운행료만 있고 청구가 없는 노선입니다(사내·공차 등 확인용).</CalcNote>
        <span className="ml-auto text-xs text-ink-soft">{kind} {tot.n}{kind === '일반' ? '노선' : '접수'} 중 비수익 {tot.loss} · 손실 합 {money(tot.lossAmt)}</span>
      </div>
      {error && <ErrorBox message={error} />}
      {!rows && !error && <div className="py-8 text-center text-sm text-ink-soft">세는 중…</div>}
      {rows && view === 'list' && (
        <div className="card max-h-[calc(100vh-12rem)] overflow-auto">
          <table className="w-full min-w-[56rem] text-sm tabular-nums">
            <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
              <tr className="border-b border-line">
                <th className="px-2 py-1.5 text-left">고객</th>
                <th className="px-2 py-1.5 text-left">{kind === '일반' ? '노선' : '접수'}</th>
                <th className="px-2 py-1.5 text-left">버스인접수담당자</th>
                {kind === '일반' && <th className="px-2 py-1.5 text-left">소속</th>}
                <th className="px-2 py-1.5 text-right">{kind === '일반' ? '배정' : '노선'}</th>
                <th className="px-2 py-1.5 text-right">청구액</th><th className="px-2 py-1.5 text-right">운행료(영업계)</th>
                {kind === '통근' && <th className="px-2 py-1.5 text-right" title="직영차량 비용 배분">직영 배분</th>}
                <th className="px-2 py-1.5 text-right">수익</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const p = profitOf(r);
                return (
                  <tr key={`${r.recv_id}|${r.dispt_id ?? ''}`} className="border-b border-line/60 hover:bg-surface-soft">
                    <td className="px-2 py-1">{r.cust_name}</td>
                    <td className="px-2 py-1">
                      <a className="text-brand-700 hover:underline" href={`/settle?tab=cust&recv=${r.recv_id}&mon=${mon}`}>{kind === '일반' ? (r.name || `노선 ${r.dispt_id}`) : `접수 ${r.recv_id}`}</a>
                      {Number(r.rev) === 0 && <span className="ml-1 rounded bg-amber-50 px-1 text-xs text-amber-700">청구 0</span>}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1 text-ink-soft">{r.manager || '—'}</td>
                    {kind === '일반' && <td className="whitespace-nowrap px-2 py-1 text-ink-soft">{r.grp_name ?? '—'}</td>}
                    <td className="px-2 py-1 text-right text-ink-soft">{r.n}</td>
                    <td className="px-2 py-1 text-right">{money(Number(r.rev))}</td>
                    <td className="px-2 py-1 text-right">{money(Number(r.exp))}</td>
                    {kind === '통근' && <td className="px-2 py-1 text-right text-ink-soft">{Number(r.dalloc) ? money(Number(r.dalloc)) : ''}</td>}
                    <td className={`px-2 py-1 text-right font-medium ${p < 0 ? 'text-red-700' : ''}`}>{money(p)}</td>
                  </tr>
                );
              })}
              {list.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-ink-soft">해당하는 줄이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {rows && view !== 'list' && (
        <div className="card max-h-[calc(100vh-12rem)] max-w-xl overflow-auto">
          <table className="w-full text-sm tabular-nums">
            <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
              <tr className="border-b border-line">
                <th className="px-2 py-1.5 text-left">{view === 'cust' ? '고객' : '버스인접수담당자'}</th>
                <th className="px-2 py-1.5 text-right">비수익 {kind === '일반' ? '노선' : '접수'}</th><th className="px-2 py-1.5 text-right">손실 합</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.key} className="cursor-pointer border-b border-line/60 hover:bg-surface-soft" onClick={() => { setQ(g.key === '(없음)' ? '' : g.key); setOnlyLoss(true); setView('list'); }}>
                  <td className="px-2 py-1">{g.key}</td><td className="px-2 py-1 text-right">{g.n}</td>
                  <td className="px-2 py-1 text-right text-red-700">{money(g.loss)}</td>
                </tr>
              ))}
              {groups.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-ink-soft">비수익이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
